import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation, Trans } from 'react-i18next';
import cloneDeep from 'lodash/cloneDeep';
import { isMobile } from 'react-device-detect';

import { DAY_MONTH } from '../../../constants';
import { withBrand } from '../../../providers/BrandProvider';
import { renderCSSString, renderHTMLString } from '../../../helpers';
import { getTierFormattedDate } from './ProductType/ProductItem/ProductItem';

import ProductType from './ProductType/ProductType';
import TierSaveBox from './TierSaveBox/TierSaveBox';
import TotalPriceBox from './TotalPriceBox/TotalPriceBox';
import SelectDelegateBlock from './SelectDelegateBlock/SelectDelegateBlock';
import TooltipMobile from './TooltipMobile/TooltipMobile';
import CarbonOffsetBox from './CarbonOffsetBox/CarbonOffsetBox';

import './SelectPackageForm.scss';

const SINGLE_CHOICE = 'SINGLE_CHOICE';
const MULTIPLE_CHOICE = 'MULTIPLE_CHOICE';

/*
 * Get an array of selected products IDs
 */
export function getSelectedProductsIds(state) {
  const selectedProducts = [];

  Object.keys(state.formState).forEach((productTypeId) => {
    const productType = state.formState[productTypeId];

    Object.keys(productType.products).forEach((productId) => {
      const product = productType.products[productId];

      if (product.selected) {
        selectedProducts.push(productId);
      }
    });
  });

  return selectedProducts;
}

/*
 * Unselect and/or disable bundled products if state changed for one of related products
 */
export function updateStateForBundledProducts(state, changedProductId) {
  const selectedProducts = getSelectedProductsIds(state);

  Object.keys(state.formState).forEach((productTypeId) => {
    const productType = state.formState[productTypeId];

    Object.keys(productType.products).forEach((productId) => {
      const productItem = productType.products[productId];
      const { bundledWith = [] } = productItem;

      if (productId !== changedProductId && bundledWith.length > 0) {
        productItem.disabled = !bundledWith.some((uuid) =>
          selectedProducts.includes(uuid),
        );

        if (productItem.disabled) {
          productItem.selected = false;
        }
      }
    });
  });
}

export class SelectPackageForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      quantity: 1,
      carbonOffset: '',
      formState: {},
      initialized: false,
      delegateTypeId: null,
      showTooltip: false,
    };

    this.handleSubmitForm = this.handleSubmitForm.bind(this);
    this.handleDelegateChange = this.handleDelegateChange.bind(this);
    this.handleQuantityChange = this.handleQuantityChange.bind(this);
    this.handleProductChange = this.handleProductChange.bind(this);
    this.handleOptionChange = this.handleOptionChange.bind(this);
    this.handleVatRulesChange = this.handleVatRulesChange.bind(this);
  }

  componentDidMount() {
    // Reload site products with preselected products
    this.reloadSiteProducts();
    if (isMobile) {
      document.addEventListener('scroll', this.handleTooltipClose);
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    window.scroll(this.lastPosition.x, this.lastPosition.y);
  }

  componentWillUnmount() {
    document.removeEventListener('scroll', this.handleTooltipClose);
  }

  static getDerivedStateFromProps(props, state) {
    if (state.initialized || !props.loaded) {
      return state;
    }

    // Delegate Type
    const delegateTypes = props.data.delegateTypes;
    if (delegateTypes && delegateTypes.length) {
      state.delegateTypeId = props.data.delegateTypes[0].delegateTypeId;
    }

    if (
      props.data.productTypesWithProducts ||
      props.data.optionalProductTypesWithProducts
    ) {
      [
        ...props.data.productTypesWithProducts,
        ...props.data.optionalProductTypesWithProducts,
      ].forEach((productType) => {
        // Product Type Object
        state.formState[productType.productTypeId] = {
          valid: true, // Product type is valid if at least one product is selected
          primary: productType.primary,
          products: {},
          selectionType: productType.selectionType,
        };

        // Check if product type has preselected product. If not - make selected none product item (Single choice only)
        let hasPreselectedProduct = false;

        productType.products.forEach((product) => {
          const selected = product.preselected;

          // Set product as selected if only one primary product available (Multiple choice only)
          let primarySelected = false;
          if (
            productType.primary &&
            props.data.productTypesWithProducts.length === 1 &&
            props.data.productTypesWithProducts[0].products.length === 1
          ) {
            primarySelected = true;
          }

          if (selected) {
            hasPreselectedProduct = true;
          }

          // All bundled primary products ids
          const bundledWith = (product.bundledWith || []).map(
            ({ mandatoryProductId }) => mandatoryProductId,
          );

          // Set product as disabled if it's bundled with primary product
          let disabled = bundledWith.length > 0;

          // Disable optional products if bundled products aren't selected
          if (bundledWith.length) {
            props.data.productTypesWithProducts.forEach((productType) => {
              productType.products.forEach((productItem) => {
                if (
                  bundledWith.indexOf(productItem.id) > -1 &&
                  productItem.preselected
                ) {
                  disabled = false;
                }
              });
            });
          }

          // Product Object
          state.formState[productType.productTypeId].products[product.id] = {
            selected: disabled ? false : selected || primarySelected,
            disabled,
            bundledWith,
            primarySelected,
            optionsList: {},
          };

          const productItem =
            state.formState[productType.productTypeId].products[product.id];

          product.productOptionsList.forEach(
            ({ type, min, max, options = [] }, optionListIndex) => {
              // Options List Object
              productItem.optionsList[optionListIndex] = {
                valid: true, // Options list is valid if it matches all requirements (min, max)
                selectionType: type,
                options: {},
                min: type === SINGLE_CHOICE ? 1 : min, // Set min to 1 in single choice mode
                max,
              };

              options.forEach((option, index) => {
                productItem.optionsList[optionListIndex].options[option.id] = {
                  selected:
                    option.preselected ||
                    (type === SINGLE_CHOICE && index === 0), // Set first item as selected in single choice mode
                  disabled: false,
                };
              });
            },
          );
        });

        // None Product Object (Single choice only. None Product id is always `none`)
        if (productType.selectionType === SINGLE_CHOICE) {
          if (
            (productType.primary &&
              props.data.productTypesWithProducts.length > 1) ||
            !productType.primary
          ) {
            state.formState[productType.productTypeId].products.none = {
              disabled: false,
              selected: !hasPreselectedProduct, // Set None Product as selected if no one product of current product type isn't selected
              bundledWith: [],
              optionsList: {},
            };
          }
        }
      });
    }

    // Tax schemes form fields
    const vatInputs = props.data?.vatForm?.vatInputs;
    if (vatInputs) {
      vatInputs.forEach((inputField) => {
        state[inputField.name] = inputField.value;
      });
    }

    state.initialized = true;
    return state;
  }

  handleTooltipVisibility = () => {
    return () => {
      isMobile &&
        this.setState((prevState) => {
          return { showTooltip: !prevState.showTooltip };
        });
    };
  };

  handleTooltipClose = () => {
    if (this.state.showTooltip) {
      this.setState({ showTooltip: false });
    }
  };

  reloadSiteProducts() {
    let {
      quantity,
      delegateTypeId,
      formState,
      country,
      region,
      taxIdExemption,
      vatNumber,
      resident,
      carbonOffset,
    } = this.state;

    quantity = parseInt(quantity, 10);
    if (isNaN(quantity) || quantity < 0) {
      return false;
    }

    const productIds = [];
    Object.keys(formState).forEach((productTypeId) => {
      Object.keys(formState[productTypeId].products).forEach((productId) => {
        const selected = formState[productTypeId].products[productId].selected;
        if (selected && productId !== 'none') {
          productIds.push(productId);
        }
      });
    });

    this.props.onChange({
      quantity,
      delegateTypeId,
      productIds,
      country,
      region,
      taxIdExemption,
      vatNumber,
      resident,
      carbonOffset,
    });
  }

  validateForm(callback = () => {}) {
    const { formState: stateObject } = this.state;
    const formState = cloneDeep(stateObject);

    Object.keys(formState).forEach((productTypeId) => {
      const selectedProducts = Object.keys(formState[productTypeId].products)
        .filter((id) => id !== 'none')
        .map((productId) => {
          return formState[productTypeId].products[productId];
        })
        .filter((product) => product.selected);

      // At least one primary product must be selected
      formState[productTypeId].valid = selectedProducts.length > 0;

      selectedProducts.forEach((product) => {
        Object.keys(product.optionsList).forEach((optionListIndex) => {
          const optionList = product.optionsList[optionListIndex];

          const min = optionList.min || false;
          const max = optionList.max || false;

          const selectedOptions = Object.keys(optionList.options)
            .map((optionId) => optionList.options[optionId])
            .filter((option) => option.selected);

          // Option is valid if it satisfies conditions
          if (min && max) {
            optionList.valid =
              selectedOptions.length >= min && selectedOptions.length <= max;
          } else if (min) {
            optionList.valid = selectedOptions.length >= min;
          } else if (max) {
            optionList.valid = selectedOptions.length <= max;
          }
        });
      });
    });

    this.setState({ formState }, () => callback());
  }

  isValidForm() {
    const { quantity, formState } = this.state;
    const intQuantity = parseInt(quantity, 10);

    // Quantity must be > 0
    if (isNaN(intQuantity) || intQuantity < 1) {
      return false;
    }

    const productTypes = Object.keys(formState).map(
      (productTypeId) => formState[productTypeId],
    );
    const validPrimaryProductTypes = productTypes.filter(
      (productType) => productType.primary && productType.valid,
    );

    const invalidProductOptions = [];
    productTypes.forEach((productType) => {
      Object.keys(productType.products).forEach((productId) => {
        if (!productType.products[productId].selected) {
          return false;
        }

        Object.keys(productType.products[productId].optionsList).forEach(
          (optionListIndex) => {
            const optionList =
              productType.products[productId].optionsList[optionListIndex];

            if (!optionList.valid) {
              invalidProductOptions.push(optionList);
            }
          },
        );
      });
    });

    // At least one of primary products of all product types must be selected
    // Option lists of all selected products must be valid
    const validationStatus =
      validPrimaryProductTypes.length > 0 && invalidProductOptions.length === 0;

    // Scroll page to make first error visible if form is invalid
    const scrollToError = this.props.scrollToError;
    const isErrorHandlerPresented =
      !validationStatus && scrollToError && typeof scrollToError === 'function';
    if (isErrorHandlerPresented) {
      scrollToError();
    }

    return validationStatus;
  }

  addToBasket() {
    const {
      carbonOffset,
      quantity,
      delegateTypeId,
      formState,
      country,
      region,
      taxIdExemption,
      vatNumber,
      resident,
    } = this.state;

    const packageSelection = [];
    Object.keys(formState).forEach((productTypeId) => {
      Object.keys(formState[productTypeId].products).forEach((productId) => {
        const productItem = formState[productTypeId].products[productId];

        if (
          productItem.selected &&
          !productItem.disabled &&
          productId !== 'none'
        ) {
          const selectedItem = {
            productId,
            optionIds: [],
          };

          Object.keys(productItem.optionsList).forEach((optionListIndex) => {
            Object.keys(
              productItem.optionsList[optionListIndex].options,
            ).forEach((optionId) => {
              const optionItem =
                productItem.optionsList[optionListIndex].options[optionId];

              if (optionItem.selected) {
                selectedItem.optionIds.push(optionId);
              }
            });
          });

          packageSelection.push(selectedItem);
        }
      });
    });

    this.props.onSubmit({
      carbonOffset,
      quantity,
      delegateTypeId,
      packageSelection,
      country,
      region,
      taxIdExemption,
      vatNumber,
      resident,
    });
  }

  handleSubmitForm() {
    this.validateForm(() => {
      if (this.isValidForm()) {
        this.addToBasket();
      }
    });
  }

  handleQuantityChange(event) {
    const quantity = event.target.value;

    if (parseInt(quantity) > 999) {
      return false;
    }

    this.setState(
      () => ({ quantity }),
      () => this.reloadSiteProducts(),
    );
  }

  handleCarbonOffsetChange = (carbonOffsetValue) => {
    this.setState(
      () => ({
        carbonOffset:
          carbonOffsetValue === this.state.carbonOffset
            ? ''
            : carbonOffsetValue,
      }),
      () => this.reloadSiteProducts(),
    );
  };

  handleDelegateChange(event) {
    const delegateTypeId = event.target.value;

    this.setState(
      () => ({ delegateTypeId }),
      () => this.reloadSiteProducts(),
    );
  }

  handleProductChange(event) {
    const checked = event.target.checked;
    const [productTypeId, productId] = event.target.value.split(':');
    const selectionType = this.state.formState[productTypeId].selectionType;
    const primarySelected =
      this.state.formState[productTypeId].products[productId].primarySelected;

    if (primarySelected) {
      return false;
    }

    if (selectionType === SINGLE_CHOICE) {
      this.setState(
        (prevState) => {
          Object.keys(prevState.formState[productTypeId].products).forEach(
            (productId) => {
              prevState.formState[productTypeId].products[
                productId
              ].selected = false;
            },
          );

          prevState.formState[productTypeId].products[
            productId
          ].selected = true;

          updateStateForBundledProducts(prevState, productId);

          return {
            formState: prevState.formState,
          };
        },
        () => this.reloadSiteProducts(),
      );
    }

    if (selectionType === MULTIPLE_CHOICE) {
      this.setState(
        (prevState) => {
          prevState.formState[productTypeId].products[productId].selected =
            checked;

          updateStateForBundledProducts(prevState, productId);

          return {
            formState: prevState.formState,
          };
        },
        () => this.reloadSiteProducts(),
      );
    }
  }

  handleOptionChange(event) {
    const [productTypeId, productId, optionId, optionListIndex] =
      event.target.value.split(':');
    const selectionType =
      this.state.formState[productTypeId].products[productId].optionsList[
        optionListIndex
      ].selectionType;

    const checked = event.target.checked;

    if (selectionType === SINGLE_CHOICE) {
      this.setState((prevState) => {
        const optionList =
          prevState.formState[productTypeId].products[productId].optionsList[
            optionListIndex
          ];

        Object.keys(optionList.options).forEach((optionId) => {
          optionList.options[optionId].selected = false;
        });

        optionList.options[optionId].selected = true;

        return {
          formState: prevState.formState,
        };
      });
    }

    if (selectionType === MULTIPLE_CHOICE) {
      this.setState((prevState) => {
        const optionList =
          prevState.formState[productTypeId].products[productId].optionsList[
            optionListIndex
          ];

        optionList.options[optionId].selected = checked;

        const min = optionList.min || false;
        const max = optionList.max || false;

        const options = Object.keys(optionList.options).map(
          (optionId) => optionList.options[optionId],
        );

        if (max) {
          const notSelectedOptions = options.filter(
            (option) => !option.selected,
          );

          if (options.length - max >= notSelectedOptions.length) {
            notSelectedOptions.forEach((option) => {
              option.disabled = true;
            });
          } else {
            notSelectedOptions.forEach((option) => {
              option.disabled = false;
            });
          }
        }

        const selectedOptions = options.filter((option) => option.selected);

        if (min && max) {
          optionList.valid =
            selectedOptions.length >= min && selectedOptions.length <= max;
        } else if (min) {
          optionList.valid = selectedOptions.length >= min;
        } else if (max) {
          optionList.valid = selectedOptions.length <= max;
        }

        return {
          formState: prevState.formState,
        };
      });
    }
  }

  handleVatRulesChange(vatRulesDetails = {}) {
    const { fieldName, fieldValue } = vatRulesDetails;

    if (fieldValue !== this.state[fieldName]) {
      const newFieldValue = {};
      newFieldValue[fieldName] = fieldValue;
      this.setState(
        () => {
          return newFieldValue;
        },
        () => {
          if (fieldName !== 'vatNumber') this.reloadSiteProducts();
        },
      );
    }
  }

  getSnapshotBeforeUpdate(prevProps, prevState) {
    this.lastPosition = {
      x: window.pageXOffset || document.documentElement.scrollLeft,
      y: window.pageYOffset || document.documentElement.scrollTop,
    };

    return null;
  }

  render() {
    const { t } = this.props;

    let {
      meta: { totalCalculation = {} } = {},
      deliveryType,
      data: {
        vatRules = false,
        packageDescription = '',
        packageDiscountsDescriptions = [],
        productTitle,
        productTitleSize = 'H1',
        delegateTypes = [],
        nonPrimaryTitle = t('event.purchase.extras'),
        taxLabelStyle = 'VAT',
        priceInclVat,
        vatRate,
        tier = {},
        vatForm: { vatInputs = [], disabled } = {},
        productTypesWithProducts: primaryProductTypes = [],
        optionalProductTypesWithProducts: optionalProductTypes = [],
        carbonOffsetForm,
      } = {},
      header: { dateFormat = DAY_MONTH } = {},
      brand: { secondaryColor } = {},
      pageConfig: { location } = {},
      errors,
    } = this.props;

    const Heading = productTitleSize.toLowerCase();
    const headingStyle = { color: secondaryColor };
    const { showTooltip } = this.state;

    if (!vatRate) {
      vatRate = 0;
    } else if (typeof vatRate === 'string') {
      vatRate = parseFloat(vatRate);
    }

    // Find all checked primary product types
    const checkedPrimaryProductTypes = Object.keys(this.state.formState)
      .map((productTypeId) => {
        return this.state.formState[productTypeId];
      })
      .filter((productType) => productType.primary && productType.valid);

    // Check if at least one primary product is selected
    const primarySelectionError = checkedPrimaryProductTypes.length === 0;

    return (
      <section className="c-select-package-form">
        <div className="container">
          <div className="row">
            <div className="col-xs-12 col-sm-6">
              <div className="c-select-package-form__page-content">
                {productTitle && (
                  <Heading
                    className="c-select-package-form__page-title"
                    style={headingStyle}
                  >
                    {productTitle}
                  </Heading>
                )}
                {renderHTMLString(packageDescription, location)}
                {packageDiscountsDescriptions.length > 0 && (
                  <div className="c-select-package-form__page-content__package-discounts-description">
                    <div className="title">
                      {t('event.purchase.package-discounts-available')}
                    </div>
                    {packageDiscountsDescriptions.map((item, key) => {
                      return (
                        <div key={key} className="description">
                          {item}
                        </div>
                      );
                    })}
                  </div>
                )}
                {renderCSSString(`
                    .c-select-package-form__page-content h2 {
                      color: ${secondaryColor};
                    }
                    .c-form-box-input__box::before {
                      border-color: ${secondaryColor};
                    }
                    .c-form-box-input--radio input:checked + .c-form-box-input__box::before {
                      background-color: ${secondaryColor};
                    }
                    .c-form-box-input--checkbox input:checked + .c-form-box-input__box::before {
                      background-color: ${secondaryColor};
                    }
                  `)}
              </div>
            </div>
            <div className="col-xs-12 col-sm-6">
              {delegateTypes.length > 0 && (
                <SelectDelegateBlock
                  selectedId={this.state.delegateTypeId}
                  delegateTypes={delegateTypes}
                  onDelegateChange={this.handleDelegateChange}
                  location={location}
                />
              )}
              {tier.maxSaving && tier.endDate && (
                <TierSaveBox
                  handleTooltipVisibility={this.handleTooltipVisibility}
                  tooltipText={t('site.purchase.applicable-savings-tip')}
                >
                  <Trans
                    i18nKey="event.purchase.save-upto-by-date"
                    values={{
                      0: tier.maxSaving,
                      1: getTierFormattedDate(tier.endDate, dateFormat),
                    }}
                  />
                </TierSaveBox>
              )}
              {primaryProductTypes.length > 0 && (
                <h4
                  className="c-select-package-form__heading"
                  style={{ color: secondaryColor }}
                >
                  {primaryProductTypes.length === 1 &&
                  primaryProductTypes[0].products.length === 1
                    ? t('event.purchase.base-booking-price')
                    : t('event.purchase.create-your-package')}
                </h4>
              )}
              {primarySelectionError && (
                <h4 className="c-select-package-form__description has-error">
                  {primaryProductTypes.length === 1 &&
                    t('event.purchase.create-your-package.error.singular', {
                      0: primaryProductTypes[0].name,
                    })}
                  {primaryProductTypes.length > 1 &&
                    t('event.purchase.create-your-package.error.multiple', {
                      0: primaryProductTypes[0].name,
                      1: primaryProductTypes[1].name,
                    })}
                </h4>
              )}
              {(primaryProductTypes.length > 1 ||
                (primaryProductTypes.length === 1 &&
                  primaryProductTypes[0].products.length > 1)) &&
                !primarySelectionError && (
                  <h4 className="c-select-package-form__description">
                    {t('event.purchase.create-your-package.instructions')}
                  </h4>
                )}
              {primaryProductTypes.map((productType) => (
                <ProductType
                  key={productType.productTypeId}
                  productType={productType}
                  tier={tier}
                  vatRate={vatRate}
                  vatRules={vatRules}
                  taxLabelStyle={taxLabelStyle}
                  priceInclVat={priceInclVat}
                  selected={this.state.formState}
                  onProductChange={this.handleProductChange}
                  onOptionChange={this.handleOptionChange}
                />
              ))}
              {optionalProductTypes.length > 0 && (
                <h4
                  className="c-select-package-form__heading"
                  style={{ color: secondaryColor }}
                >
                  {nonPrimaryTitle}
                </h4>
              )}
              {optionalProductTypes.map((productType) => (
                <ProductType
                  key={productType.productTypeId}
                  productType={productType}
                  tier={tier}
                  vatRate={vatRate}
                  vatRules={vatRules}
                  taxLabelStyle={taxLabelStyle}
                  priceInclVat={priceInclVat}
                  selected={this.state.formState}
                  onProductChange={this.handleProductChange}
                  onOptionChange={this.handleOptionChange}
                />
              ))}
              {carbonOffsetForm && (
                <CarbonOffsetBox
                  carbonOffset={this.state.carbonOffset}
                  onCarbonOffsetChange={this.handleCarbonOffsetChange}
                />
              )}
              <TotalPriceBox
                totalCalculation={totalCalculation}
                quantity={this.state.quantity}
                carbonOffset={this.state.carbonOffset}
                deliveryType={deliveryType}
                tier={tier}
                vatRate={vatRate}
                vatFormIsDisabled={disabled}
                vatInputs={vatInputs}
                errors={errors}
                taxLabelStyle={taxLabelStyle}
                priceInclVat={priceInclVat}
                dateFormat={dateFormat}
                onAddToBasket={this.handleSubmitForm}
                onQuantityChange={this.handleQuantityChange}
                onVatRulesChange={this.handleVatRulesChange}
              />
            </div>
          </div>
        </div>
        <TooltipMobile
          handleTooltipVisibility={this.handleTooltipVisibility}
          showTooltip={showTooltip}
          text={t('site.purchase.applicable-savings-tip')}
        />
      </section>
    );
  }
}

const productTypes = PropTypes.arrayOf(
  PropTypes.shape({
    productTypeId: PropTypes.string.isRequired,
    selectionType: PropTypes.oneOf([SINGLE_CHOICE, MULTIPLE_CHOICE]).isRequired,
    name: PropTypes.string.isRequired,
    noneLabel: PropTypes.string,
    primary: PropTypes.bool.isRequired,
    products: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        description: PropTypes.string,
        preselected: PropTypes.bool,
        price: PropTypes.string,
        priceInclVat: PropTypes.string,
        vat: PropTypes.string,
        tieredSaving: PropTypes.string,
        bundledWith: PropTypes.arrayOf(
          PropTypes.shape({
            mandatoryProductId: PropTypes.string.isRequired,
          }),
        ),
        productOptionsList: PropTypes.arrayOf(
          PropTypes.shape({
            options: PropTypes.arrayOf(
              PropTypes.shape({
                id: PropTypes.string.isRequired,
                name: PropTypes.string.isRequired,
                optionId: PropTypes.string,
                preselected: PropTypes.bool,
              }),
            ),
            type: PropTypes.oneOf([SINGLE_CHOICE, MULTIPLE_CHOICE]).isRequired,
          }),
        ),
      }),
    ).isRequired,
  }),
);

SelectPackageForm.propTypes = {
  brand: PropTypes.shape({
    secondaryColor: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  scrollToError: PropTypes.func,
  loaded: PropTypes.bool.isRequired,
  errors: PropTypes.object,
  meta: PropTypes.shape({
    totalCalculation: PropTypes.shape({
      subtotal: PropTypes.string,
      subtotalInclVat: PropTypes.string,
      total: PropTypes.string,
      totalInclVat: PropTypes.string,
      totalVat: PropTypes.string,
      packageDiscount: PropTypes.string,
      tieredSaving: PropTypes.string,
      basketQuantity: PropTypes.number,
      basketTotalIncVat: PropTypes.string,
      basketTotalVat: PropTypes.string,
      vatForm: PropTypes.shape({
        vatInputs: PropTypes.array,
      }),
    }),
  }).isRequired,
  deliveryType: PropTypes.string,
  data: PropTypes.shape({
    tier: PropTypes.shape({
      endDate: PropTypes.string,
      maxSaving: PropTypes.string,
    }),
    currency: PropTypes.oneOf(['GBP', 'USD', 'EUR', 'AED', 'CHF', 'AUD']),
    vatRules: PropTypes.bool,
    priceInclVat: PropTypes.bool,
    nonPrimaryTitle: PropTypes.string,
    packageDescription: PropTypes.string,
    productTitle: PropTypes.string,
    productTitleSize: PropTypes.string,
    taxLabelStyle: PropTypes.string,
    vatRate: PropTypes.string,
    packageDiscountsDescriptions: PropTypes.arrayOf(PropTypes.string),
    delegateTypes: PropTypes.arrayOf(
      PropTypes.shape({
        delegateTypeId: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        description: PropTypes.string,
      }),
    ),
    productTypesWithProducts: productTypes,
    optionalProductTypesWithProducts: productTypes,
    carbonOffsetForm: PropTypes.object,
  }).isRequired,
};

export default connect((state) => ({
  header: state.siteHeader.data,
  pageConfig: state.pageConfig,
}))(withTranslation()(withBrand(SelectPackageForm)));
